import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { milestones, principlesList, siteInfo, statsList, teamQuotes } from "@/mocks/site";

export default function About() {
  useSeo({
    title: "关于我们 - 爆料网上的定位、内容原则与发展历程",
    description:
      "了解爆料网上是谁：我们专注热点事件始末追踪，说明站点定位、内容原则、时间线整理方法与联系方式，帮助读者更好地使用这份事件资料库。",
    keywords: "关于爆料网上,爆料网上介绍,事件追踪原则,爆料网上定位",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们",
        url: "https://huahuoyouxi.cn/about",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "爆料网上",
        url: "https://huahuoyouxi.cn/about",
        email: siteInfo.email,
        description: siteInfo.description,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://huahuoyouxi.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://huahuoyouxi.cn/about" },
        ],
      },
    ],
  });

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-5 md:px-10 pt-24 md:pt-32 pb-10 md:pb-14">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 text-xs tracking-[0.22em] text-primary-600 font-medium">
              <span className="w-5 h-px bg-primary-400" />
              关于我们
            </span>
            <h1 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              我们只做一件事：把事件的来龙去脉整理清楚
            </h1>
            <p className="mt-4 text-sm md:text-base leading-loose text-foreground-700">
              {siteInfo.name} 是一个以时间线为核心的热点事件资料站点。我们不做情绪的放大器，
              而是把散落在各处的进展、回应与更正收集起来，按时间顺序排好，
              让读者用几分钟就能掌握一起事件的完整脉络。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-5 md:px-10 py-12 md:py-16 bg-background-50">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {statsList.map((stat) => (
            <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="font-heading text-xl md:text-3xl font-bold text-primary-600">
                {stat.value}
                <span className="text-accent-600 text-base md:text-lg">{stat.suffix}</span>
              </p>
              <p className="mt-1.5 text-xs md:text-sm text-foreground-600">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-5 md:px-10 py-12 md:py-16 bg-background-100">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
          <div className="lg:w-[36%]">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              网站定位
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              面向关注热点事件发展的普通读者，提供结构清晰、可回溯的事件整理。
            </p>
          </div>
          <div className="lg:w-[64%] grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-focus-3-line text-primary-600" />
                我们在意什么
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                信息的完整度与可核对性。一个节点如果找不到可见来源，我们宁可不写，也不凭印象补全。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-user-voice-line text-accent-600" />
                我们不做评判
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                各方说法都保留位置，把判断留给读者。只有出现明确结论时，才会单独标注说明依据。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-timer-flash-line text-primary-600" />
                不追速度，追准确
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                事件刚发生时信息混乱，我们更愿意等一等，把矛盾之处标出来，而不是抢先给出一个说法。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-refresh-line text-accent-600" />
                错了就更正
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                如果读者指出节点有误，我们会核实并在原处补充更正说明，而不是悄悄修改。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-5 md:px-10 py-12 md:py-16 bg-background-50">
        <div className="max-w-3xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            内容原则
          </h2>
          <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
            这几条原则决定了每一起事件会以什么样的方式被整理出来。
          </p>
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
          {principlesList.map((item) => (
            <div
              key={item.title}
              className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
            >
              <span className="shrink-0 w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 text-lg">
                <i className={item.icon} />
              </span>
              <div>
                <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-5 md:px-10 py-12 md:py-16 bg-background-100">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
          <div className="lg:w-[34%]">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              发展历程
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              从一份内部手记，到如今按统一模板整理的事件资料库。
            </p>
          </div>
          <ol className="lg:w-[66%]">
            {milestones.map((item) => (
              <li key={item.year} className="relative pl-6 pb-8 last:pb-0 border-l border-background-300">
                <span className="absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full bg-accent-500 ring-4 ring-background-100" />
                <span className="font-heading text-sm font-bold text-accent-700">{item.year}</span>
                <h3 className="mt-1 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="w-full px-5 md:px-10 py-12 md:py-16 bg-background-50">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          读者口碑
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {teamQuotes.map((quote) => (
            <figure
              key={quote.name}
              className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
            >
              <i className="ri-double-quotes-l text-accent-500 text-2xl" />
              <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">
                {quote.text}
              </blockquote>
              <figcaption className="mt-4 pt-4 border-t border-background-200 text-xs text-foreground-500">
                {quote.name} · {quote.role}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="w-full px-5 md:px-10 py-12 md:py-16 bg-background-100">
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              联系与线索提供
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              内容更正、线索提供或合作咨询，都可以通过下面的方式找到我们。
              涉及需要核实的细节，我们会尽量在两到三个工作日内回复。
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-800">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-mail-line text-primary-600" />
                {siteInfo.email}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-mail-open-line text-primary-600" />
                {siteInfo.workingEmail}
              </span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:shrink-0">
            <Link
              to="/#tips"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-upload-2-line" />
              提交线索
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap border border-primary-300 text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer"
            >
              查看常见问题
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}